import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Modal, Btn } from './ui';

function median(nums) {
  if (!nums.length) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function formatSec(sec) {
  return sec.toFixed(2);
}

// Stopwatch with laps for taking a manual cycle-time reading on the shop
// floor. Each "Lap" records the time since the previous lap (i.e. the
// duration of one cycle), not cumulative elapsed time. "Use this value"
// applies the median of all recorded laps — a standard, outlier-resistant
// way to arrive at a representative cycle time from several hand-timed reps.
export default function StopwatchModal({ open, onClose, onApply, subtitle }) {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0); // seconds, current in-progress lap
  const [laps, setLaps] = useState([]);
  const startRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setElapsed((Date.now() - startRef.current) / 1000);
      }, 50);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [running]);

  // Reset everything each time the modal is freshly opened
  useEffect(() => {
    if (open) {
      setRunning(false);
      setElapsed(0);
      setLaps([]);
    }
  }, [open]);

  const handleStart = () => {
    startRef.current = Date.now();
    setElapsed(0);
    setRunning(true);
  };

  const handleLap = () => {
    const now = Date.now();
    const cycleSec = (now - startRef.current) / 1000;
    setLaps(prev => [...prev, cycleSec]);
    startRef.current = now;
    setElapsed(0);
  };

  const handleStop = () => setRunning(false);

  const handleReset = () => {
    setRunning(false);
    setElapsed(0);
    setLaps([]);
  };

  const handleDeleteLap = (idx) => setLaps(prev => prev.filter((_, i) => i !== idx));

  const med = useMemo(() => median(laps), [laps]);

  const handleApply = () => {
    if (med != null) {
      // The stopwatch measures in seconds (natural for hand-timing a cycle),
      // but SAM/cycle_time throughout Skillflo are in minutes. Convert here,
      // once, so every caller of this component gets the right unit.
      const minutes = med / 60;
      onApply(minutes.toFixed(4));
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Stopwatch — cycle time" width={340}>
      {subtitle && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 10 }}>{subtitle}</div>}

      <div style={{ textAlign: 'center', margin: '4px 0 16px' }}>
        <div style={{ fontSize: 40, fontWeight: 700, fontFamily: 'monospace', color: running ? '#185FA5' : '#1a1a1a' }}>
          {formatSec(elapsed)}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 2 }}>
          {running ? 'current cycle…' : laps.length ? 'stopped' : 'ready'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {!running && laps.length === 0 && (
          <button onClick={handleStart} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#185FA5', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Start</button>
        )}
        {running && (
          <>
            <button onClick={handleLap} style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: '#185FA5', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Lap</button>
            <button onClick={handleStop} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid rgba(0,0,0,0.15)', background: '#fff', color: '#1a1a1a', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Stop</button>
          </>
        )}
        {!running && laps.length > 0 && (
          <>
            <button onClick={handleStart} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#185FA5', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Restart</button>
            <button onClick={handleReset} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid rgba(0,0,0,0.15)', background: '#fff', color: '#1a1a1a', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Reset</button>
          </>
        )}
      </div>

      {laps.length > 0 && (
        <div style={{ maxHeight: 160, overflowY: 'auto', border: '0.5px solid var(--border-md)', borderRadius: 8, marginBottom: 14 }}>
          {laps.map((l, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '6px 10px', fontSize: 12.5,
              borderBottom: i < laps.length - 1 ? '0.5px solid var(--border)' : 'none',
              background: med === l ? 'var(--acc-bg)' : 'transparent'
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>Lap {i + 1}</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{formatSec(l)}s</span>
              <a onClick={() => handleDeleteLap(i)} style={{ fontSize: 11, color: 'var(--red)', cursor: 'pointer' }}>Remove</a>
            </div>
          ))}
        </div>
      )}

      {laps.length > 0 && (
        <div style={{ background: 'var(--acc-bg)', borderRadius: 8, padding: '10px 12px', marginBottom: 14, textAlign: 'center' }}>
          <div style={{ fontSize: 10.5, color: 'var(--acc)', fontWeight: 600, textTransform: 'uppercase' }}>Median of {laps.length} lap{laps.length > 1 ? 's' : ''}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--acc)', fontFamily: 'monospace' }}>{formatSec(med)}s</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>= {(med / 60).toFixed(4)} min — this is what gets applied</div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={handleApply} disabled={med == null}>Use this value</Btn>
      </div>
    </Modal>
  );
}